import { Widget } from '../features/widgets/widgetsApi';

/**
 * Generates a comprehensive set of replacement pairs for widget templates.
 * This ensures compatibility across multiple widget dialects and app types.
 */
export const getWidgetReplacements = (widget: Partial<Widget> | any, externalScrollerSettings?: any): Array<[string, string]> => {
    let sourceData = { ...widget };

    // Recovery logic: if we're updating an existing widget setting that lost its source fields
    // but still has the compiled 'widgetData' JSON, we extract the fields from there.
    if (!sourceData.latitude && !sourceData.rss_feed_url && widget.replacements) {
        const widgetDataPair = widget.replacements.find(([k]: any) => k === 'widgetData');
        if (widgetDataPair && typeof widgetDataPair[1] === 'string') {
            try {
                const parsed = JSON.parse(widgetDataPair[1]);
                sourceData = { ...parsed, ...sourceData };
            } catch (e) {
                console.error('Failed to recover widget data from replacements', e);
            }
        }
    }

    const universalConfig = {
        // Basic Metadata
        name: sourceData.name || '',
        text: sourceData.description || sourceData.content || sourceData.text || 'Sample Text',
        description: sourceData.description || sourceData.content || sourceData.text || 'Sample Text',

        // Visual Styles
        isTransparent: externalScrollerSettings?.isTransparent || sourceData.isTransparent || false,
        backgroundColor: (externalScrollerSettings?.isTransparent || sourceData.isTransparent) ? 'transparent' : (externalScrollerSettings?.backgroundColor || sourceData.background_color || sourceData.backgroundColor || '#FFFFFF'),
        textColor: externalScrollerSettings?.textColor || sourceData.text_color || sourceData.textColor || '#000000',
        fontFamily: externalScrollerSettings?.fontFamily || sourceData.font_family || sourceData.fontFamily || 'Arial',
        fontWeight: sourceData.font_weight || sourceData.fontWeight || 'normal',
        textStyle: externalScrollerSettings?.textStyle || sourceData.font_style || sourceData.textStyle || 'normal',
        alignment: sourceData.alignment || 'left',
        direction: externalScrollerSettings?.direction || sourceData.direction || sourceData.alignment || 'rtl',

        // Functional Settings
        scrollSpeed: externalScrollerSettings?.scrollSpeed || sourceData.scrollSpeed || sourceData.scroll_speed || sourceData.speed || 'medium',

        // RSS / Data Specific
        rss_feed_url: sourceData.rss_feed_url || sourceData.feedUrl || '',
        rss_display_type: sourceData.rss_display_type || sourceData.displayUnit || 'ticker',
        grid_preview: sourceData.gridPreview || false,
        duration: sourceData.duration || '10',

        // Location / Weather / Clock
        latitude: sourceData.latitude || '',
        longitude: sourceData.longitude || '',
        location: sourceData.location || sourceData.CurrentLocation || '',
        time_zone: sourceData.time_zone || sourceData.TimeZone || '',
        temperature: sourceData.temperature || '',

        // Scroller Specific Content
        content: externalScrollerSettings?.content || sourceData.content || sourceData.description || sourceData.text || 'Sample Text',

        // Global metadata
        live_app_url: sourceData.live_app_url || '',
        file_type: sourceData.file_type || 'widget',
        items: sourceData.items || [{ text: externalScrollerSettings?.content || sourceData.description || sourceData.content || 'Sample Text' }]
    };

    const configJson = JSON.stringify(universalConfig);

    // Map to multiple common global variable names used by various widget scripts
    const replacements: Array<[string, string]> = [
        ['widgetData', configJson],
        ['textWidgetData', configJson],
        ['scrollData', configJson],
        ['weatherData', configJson],
        ['clockData', configJson],
        ['rssData', configJson]
    ];

    // Specific field injections for older widgets that don't parse JSON
    if (universalConfig.latitude) replacements.push(['latitude', String(universalConfig.latitude)]);
    if (universalConfig.longitude) replacements.push(['longitude', String(universalConfig.longitude)]);
    if (universalConfig.location) replacements.push(['CurrentLocation', universalConfig.location]);
    if (universalConfig.time_zone) replacements.push(['TimeZone', universalConfig.time_zone]);
    // Consolidate RSS / BBC News data for better compatibility
    const rssUrl = universalConfig.rss_feed_url || sourceData.rss_feed_url || sourceData.feedUrl || sourceData.newsTopic || '';
    if (rssUrl) {
        replacements.push(['rss_feed_url', String(rssUrl)]);
        replacements.push(['RSSURL', String(rssUrl)]);
    }

    const displayType = universalConfig.rss_display_type || sourceData.rss_display_type || sourceData.displayUnit || sourceData.themeType || '';
    if (displayType) {
        replacements.push(['rss_display_type', String(displayType)]);
        replacements.push(['type', String(displayType)]);
    }

    if (universalConfig.duration) {
        replacements.push(['duration', String(universalConfig.duration)]);
        const ms = Number(universalConfig.duration) * 1000;
        replacements.push(['5000', String(ms)]); // Replaces default 5000ms value in scripts
    }
    if (universalConfig.grid_preview !== undefined) replacements.push(['grid_preview', String(universalConfig.grid_preview)]);
    if (universalConfig.temperature) replacements.push(['temperature', String(universalConfig.temperature)]);
    if (universalConfig.backgroundColor) replacements.push(['background_color', universalConfig.backgroundColor]);
    if (universalConfig.textColor) replacements.push(['text_color', universalConfig.textColor]);

    return replacements;
};

/**
 * Generates a unique key for the widget iframe to trigger remounts on interaction end.
 */
export const getWidgetInteractionKey = (
    id: string,
    interactionState: {
        isDragging?: boolean;
        isResizing?: boolean;
        isRotating?: boolean;
        width?: number;
        height?: number;
        left?: number;
        top?: number;
    }
): string => {
    const { isDragging, isResizing, isRotating, width, height, left, top } = interactionState;

    if (isDragging || isResizing || isRotating) {
        return `${id}-active`;
    }

    return `${id}-settled-${width || 0}-${height || 0}-${left || 0}-${top || 0}`;
};
